import React, { useState } from 'react';
import { useRefreshSingleMealAndGroceryList } from '../hooks/refreshSingleMealAndGroceryList';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';

function EmptyMealCardMobile({ meal, onMealRefresh, onGroceryListRefresh, isInPast, isNewSlot, mealTime, onAddNewMeal }) {
    const { refreshSingleMealAndGroceryList, isRefreshing } = useRefreshSingleMealAndGroceryList();
    const [errorRefreshingMeal, setErrorRefreshingMeal] = useState(false);
    const [errorRefreshingMealMessage, setErrorRefreshingMealMessage] = useState('');
    const [isAddingNewMeal, setIsAddingNewMeal] = useState(false);
    const { sessionToken } = useAuth();

    const showErrorRefreshingMealToast = (message) => {
        setErrorRefreshingMealMessage(message);
        setErrorRefreshingMeal(true);
        
        setTimeout(() => {
            setErrorRefreshingMeal(false);
        }, 5000);
    };

    const handleAddRecipe = async (e) => {
        e.stopPropagation();
        
        if (isInPast) {
            return;
        }

        // If this is a new slot card, call the add new meal function
        if (isNewSlot && onAddNewMeal && mealTime) {
            try {
                setIsAddingNewMeal(true);
                await onAddNewMeal(mealTime);
                setIsAddingNewMeal(false);
            } catch (error) {
                console.error('Failed to add new meal:', error);
                setIsAddingNewMeal(false);
                showErrorRefreshingMealToast('Error adding meal. Please refresh the page and try again.');
            }
            return;
        }

        // Otherwise, this is an existing empty slot, so add a recipe to it
        try {
            await refreshSingleMealAndGroceryList(meal.id, meal.app_user_id);
            
            if (onMealRefresh) {
                onMealRefresh();
            }
            
            if (onGroceryListRefresh) {
                onGroceryListRefresh("Grocery list updated");
            }
        } catch (error) {
            console.error('Failed to add recipe:', error);
            showErrorRefreshingMealToast('Error adding recipe. Please refresh the page and try again.');
        }
    };

    const handleRemoveSlot = async (e) => {
        e.stopPropagation();
        
        if (isInPast || isNewSlot) {
            return;
        }

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            const response = await fetchWithCSRF('/api/v1/meal/delete_no_refresh', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ 
                    meal_id: meal.id,
                }),
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to remove meal slot');
            }

            if (onMealRefresh) {
                onMealRefresh();
            }
            
            if (onGroceryListRefresh) {
                onGroceryListRefresh("Meal slot removed");
            }
        } catch (error) {
            console.error('Failed to remove meal slot:', error);
            showErrorRefreshingMealToast('Error removing meal slot. Please refresh the page and try again.');
        }
    };

    return (
        <div 
            className={`meal-card-mobile empty-meal-card-mobile ${isInPast ? 'cooked' : ''}`}
            onClick={handleAddRecipe}
            style={{ 
                cursor: isInPast ? 'not-allowed' : 'pointer',
                opacity: isInPast ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '90px',
                border: '2px dashed #d0d0d0',
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '12px',
                transition: 'all 0.2s ease',
                position: 'relative'
            }}
        >
            {/* Remove slot button in top left corner - only show for existing slots */}
            {!isInPast && !isNewSlot && (
                <div 
                    className="delete-meal-corner-mobile"
                    onClick={handleRemoveSlot}
                    style={{ 
                        cursor: 'pointer'
                    }}
                >
                    <img 
                        src="/icons/x-outline.svg" 
                        alt="Remove Slot" 
                        style={{ 
                            width: '20px', 
                            height: '20px'
                        }}
                    />
                </div>
            )}
            {(isRefreshing || isAddingNewMeal) ? (
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px' 
                }}>
                    <div style={{ 
                        width: '35px', 
                        height: '35px', 
                        borderRadius: '50%',
                        border: '3px solid #f0f0f0',
                        borderTopColor: '#52cd92',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ 
                        color: '#666', 
                        fontSize: '0.85rem',
                        margin: 0
                    }}>
                        {isAddingNewMeal ? 'Adding meal...' : 'Adding recipe...'}
                    </p>
                </div>
            ) : (
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px' 
                }}>
                    <div style={{ 
                        width: '50px', 
                        height: '50px', 
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        border: '2px solid #d0d0d0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.2s ease'
                    }}>
                        <svg 
                            width="24" 
                            height="24" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="#999" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        >
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                    </div>
                    <p style={{ 
                        color: '#666', 
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        margin: 0
                    }}>
                        Add meal
                    </p>
                </div>
            )}
        </div>
    );
}

export default EmptyMealCardMobile;

